import { PlusIcon } from '@shopify/polaris-icons';
import { supplierQuoteTotals } from '../../../packages/shared/supplier-management/prices';
import { PurchasePlanner } from '../components/purchase-planning/Planner';
import { usePurchaseAccount } from '../components/purchase-planning/account';
import type { PurchaseDraft } from '../../../packages/shared/purchase-planning/contract';
import { purchaseMoney } from '../components/purchase-planning/Summary';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Select,
  DataTable,
  EmptyState,
  Spinner,
  Banner,
  Modal,
  Checkbox,
  TextField,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface Recommendation {
  product_id: number;
  sku: string;
  title: string;
  vendor: string | null;
  currency: string | null;
  supplier_name: string;
  default_supplier_id: number | null;
  lead_time_days: number;
  current_stock: number;
  available_stock: number;
  reserved_stock: number;
  units_sold_period: number;
  daily_velocity: number;
  days_until_stockout: number | null;
  recommended_qty: number;
  urgency: string;
  unit_cost: number | null;
  qty_on_order: number;
  po_expected_date: string | null;
}

interface Summary {
  total_products: number;
  urgency_counts: Record<string, number>;
}

interface Supplier {
  id: number;
  name: string;
  currency: string;
}

export default function ReorderRecommendations() {
  const access = usePurchaseAccount();
  return <ReorderPage key={JSON.stringify(access.account)} />;
}
function ReorderPage() {
  const { account, canEdit } = usePurchaseAccount();
  const [purchaseSeed, setPurchaseSeed] = useState<{key: string; draft: Partial<PurchaseDraft>} | null>(null);
  const [purchaseError, setPurchaseError] = useState('');
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();

  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [bySupplier, setBySupplier] = useState<Record<string, Recommendation[]>>({});
  const [summary, setSummary] = useState<Summary | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // Filters
  const [days, setDays] = useState('30');
  const [supplierId, setSupplierId] = useState('');
  const [urgency, setUrgency] = useState('');
  const [groupBySupplier, setGroupBySupplier] = useState(false);

  // Create PO modal
  const [createPoModal, setCreatePoModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [quantities, setQuantities] = useState<Record<number, string>>({});
  const [poNotes, setPoNotes] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);

  const loadSuppliers = useCallback(async () => {
    const result = await fetchApi<{ suppliers: Supplier[] }>('/purchase-orders/suppliers?active=true');
    if (result) {
      setSuppliers(result.suppliers || []);
    }
  }, [fetchApi]);

  const loadData = useCallback(async () => {
    let url = `/reports/reorder-recommendations?days=${days}&limit=200`;
    if (supplierId) url += `&supplier_id=${supplierId}`;
    if (urgency) url += `&urgency=${urgency}`;

    const result = await fetchApi<{
      recommendations: Recommendation[];
      by_supplier: Record<string, Recommendation[]>;
      summary: Summary;
    }>(url);

    if (result) {
      setRecommendations(result.recommendations || []);
      setBySupplier(result.by_supplier || {});
      setSummary(result.summary);
    }
  }, [fetchApi, days, supplierId, urgency]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await fetchApi<{ success: boolean; export_id: number }>(
        '/reports/reorder-recommendations/export',
        {
          method: 'POST',
          body: JSON.stringify({ days: parseInt(days), supplier_id: supplierId || null }),
        }
      );
      if (result?.success && result.export_id) {
        window.open(`/api/reports/exports/${result.export_id}/download`, '_blank');
      }
    } finally {
      setExporting(false);
    }
  };

  const openCreatePoModal = (supplierName: string) => {
    const supplierItems = bySupplier[supplierName] || [];
    const supplier = suppliers.find(s => s.name === supplierName);

    if (!supplier) {
      // Can't create PO without a valid supplier
      return;
    }

    setSelectedSupplier(supplierName);
    const itemIds = new Set(supplierItems.map(item => item.product_id));
    setSelectedItems(itemIds);

    const qtys: Record<number, string> = {};
    supplierItems.forEach(item => {
      qtys[item.product_id] = String(Math.max(item.recommended_qty, 1));
    });
    setQuantities(qtys);
    setPoNotes('');
    setCreatePoModal(true);
  };

  const handleCreatePo = () => {
    const supplier = suppliers.find(s => s.name === selectedSupplier);
    if (!supplier || !canEdit || !selectedItems.size) return;
    setPurchaseError('');
    try {
      const currencies=new Set(Array.from(selectedItems).map(id=>recommendations.find(r=>r.product_id===id)?.currency));
      if(currencies.size!==1||!Array.from(currencies)[0])throw new Error('Select products with one known offer currency. Add or review supplier offers first.');
      const currency=Array.from(currencies)[0]!;
      const lines = Array.from(selectedItems).map(productId => {
        const rec = recommendations.find(r => r.product_id === productId);
        const quantity = quantities[productId];
        if (!rec || rec.unit_cost===null || !/^\d{1,7}$/.test(quantity) || Number(quantity) < 1 || Number(quantity) > 1000000) throw new Error('Enter whole quantities from one to 1,000,000 for each selected product.');
        return {id:null, product_id:String(productId), title:rec.title, sku:rec.sku, quantity, unit_cost:String(rec.unit_cost), discount:'0', tax:'0', notes:''};
      });
      setPurchaseSeed({key:crypto.randomUUID(), draft:{supplier_id:String(supplier.id), supplier_name:supplier.name, currency, lines, notes:poNotes}});
      setCreatePoModal(false);
    } catch (e) { setPurchaseError((e as Error).message); }
  };

  const toggleItemSelection = (productId: number) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const formatCurrency = (amount:number|null,currency?:string|null) => amount===null?'Price missing':purchaseMoney(amount,currency||'');
  const totals=supplierQuoteTotals(recommendations.map(r=>({...r,quantity:r.recommended_qty})));
  const displayTotals=(t:ReturnType<typeof supplierQuoteTotals>)=>[...t.values.map(v=>formatCurrency(v.amount,v.currency)),...(t.missing?[`${t.missing} prices need review`]:[])].join(' · ');

  const formatNumber = (value: number | null) => {
    if (value === null) return '-';
    return value.toFixed(1);
  };

  const getUrgencyBadge = (urgencyLevel: string) => {
    const tones: Record<string, 'critical' | 'warning' | 'success' | 'info' | 'attention'> = {
      'Critical': 'critical',
      'Urgent': 'warning',
      'Reorder': 'attention',
      'Soon': 'info',
      'OK': 'success',
    };
    return <Badge tone={tones[urgencyLevel] || 'info'}>{urgencyLevel}</Badge>;
  };

  const supplierOptions = [
    { label: 'All Suppliers', value: '' },
    ...suppliers.map((s) => ({ label: s.name, value: s.id.toString() })),
  ];

  const urgencyOptions = [
    { label: 'All Urgency Levels', value: '' },
    { label: 'Critical Only', value: 'critical' },
    { label: 'Urgent & Critical', value: 'urgent' },
  ];

  const periodOptions = [
    { label: 'Last 7 days', value: '7' },
    { label: 'Last 14 days', value: '14' },
    { label: 'Last 30 days', value: '30' },
    { label: 'Last 60 days', value: '60' },
    { label: 'Last 90 days', value: '90' },
  ];

  const renderDataTable = (items: Recommendation[]) => {
    const rows = items.map((item) => [
      <Button variant="plain" onClick={() => navigate(`/products/${item.product_id}`)}>
        {item.sku}
      </Button>,
      <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.title}
      </div>,
      item.supplier_name,
      item.available_stock.toString(),
      formatNumber(item.daily_velocity),
      item.days_until_stockout !== null ? `${Math.round(item.days_until_stockout)}d` : '-',
      `${item.lead_time_days}d`,
      item.recommended_qty.toString(),
      formatCurrency(item.unit_cost===null?null:item.unit_cost * item.recommended_qty,item.currency),
      getUrgencyBadge(item.urgency),
      item.qty_on_order > 0 ? (
        <Badge tone="info">{`${item.qty_on_order} on order`}</Badge>
      ) : null,
    ]);

    return (
      <DataTable
        columnContentTypes={[
          'text', 'text', 'text', 'numeric', 'numeric',
          'text', 'text', 'numeric', 'numeric', 'text', 'text',
        ]}
        headings={[
          'SKU', 'Product', 'Supplier', 'Stock', 'Velocity',
          'Days Left', 'Lead Time', 'Rec. Qty', 'Est. Cost', 'Urgency', 'On Order',
        ]}
        rows={rows}
      />
    );
  };

  const modalItems = selectedSupplier ? (bySupplier[selectedSupplier] || []) : [];

  return (
    <Page
      title="Reorder Recommendations"
      subtitle="Products that need to be reordered based on sales velocity"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        content: 'Export CSV',
        onAction: handleExport,
        loading: exporting,
        disabled: recommendations.length === 0,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {summary && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Products to Reorder</Text>
                  <Text variant="heading2xl" as="p">{summary.total_products}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Need reordering</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Critical</Text>
                  <Text variant="heading2xl" as="p" tone={summary.urgency_counts.Critical > 0 ? 'critical' : undefined}>
                    {summary.urgency_counts.Critical || 0}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">Out of stock</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Urgent</Text>
                  <Text variant="heading2xl" as="p">
                    {summary.urgency_counts.Urgent || 0}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">Stock below lead time</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Est. Order Value</Text>
                  {totals.values.map(v=><Text key={v.currency} variant="headingMd" as="p">{formatCurrency(v.amount,v.currency)}</Text>)}{totals.missing>0&&<Text as="p">{totals.missing} products need a supplier price and currency</Text>}
                  <Text as="p" variant="bodySm" tone="subdued">For the suggestions shown</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        <Layout.Section><Banner tone="info">Showing up to 200 suggestions. Filter by supplier to narrow the list. Each price uses its offer currency. Review prices before placing an order.</Banner></Layout.Section>
        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="start" blockAlign="end">
              <div style={{ width: 150 }}>
                <Select
                  label="Period"
                  options={periodOptions}
                  value={days}
                  onChange={setDays}
                />
              </div>
              <div style={{ width: 180 }}>
                <Select
                  label="Supplier"
                  options={supplierOptions}
                  value={supplierId}
                  onChange={setSupplierId}
                />
              </div>
              <div style={{ width: 180 }}>
                <Select
                  label="Urgency"
                  options={urgencyOptions}
                  value={urgency}
                  onChange={setUrgency}
                />
              </div>
              <div style={{ paddingTop: 24 }}>
                <Checkbox
                  label="Group by Supplier"
                  checked={groupBySupplier}
                  onChange={setGroupBySupplier}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Data */}
        <Layout.Section>
          {loading ? (
            <Card>
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          ) : recommendations.length === 0 ? (
            <Card>
              <EmptyState
                heading="No reorder recommendations"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>All products have sufficient stock based on current velocity.</p>
              </EmptyState>
            </Card>
          ) : groupBySupplier ? (
            <BlockStack gap="400">
              {Object.entries(bySupplier).map(([supplierName, items]) => {
                const supplier = suppliers.find(s => s.name === supplierName);
                const amounts=displayTotals(supplierQuoteTotals(items.map(r=>({...r,quantity:r.recommended_qty}))));

                return (
                  <Card key={supplierName}>
                    <BlockStack gap="400">
                      <InlineStack align="space-between">
                        <BlockStack gap="100">
                          <Text variant="headingMd" as="h2">{supplierName}</Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {items.length} products | Est. value: {amounts||'No selected quantities'}
                          </Text>
                        </BlockStack>
                        {supplier && (
                          <Button icon={PlusIcon} disabled={!canEdit} onClick={() => openCreatePoModal(supplierName)}>
                            Create PO
                          </Button>
                        )}
                      </InlineStack>
                      {renderDataTable(items)}
                    </BlockStack>
                  </Card>
                );
              })}
            </BlockStack>
          ) : (
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">All Recommendations</Text>
                {renderDataTable(recommendations)}
                <Text as="p" variant="bodySm" tone="subdued">
                  {recommendations.length} products need reordering
                </Text>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      <div style={{ marginTop: 20 }}><PurchasePlanner key={purchaseSeed?.key || 'new'} account={account} target={null} canEdit={canEdit} seed={purchaseSeed?.draft} onSaved={id => {setPurchaseSeed(null); navigate('/purchase-orders/'+id);}} /></div>
      {/* Create PO Modal */}
      <Modal
        open={createPoModal}
        onClose={() => setCreatePoModal(false)}
        title={`Create Purchase Order - ${selectedSupplier}`}
        primaryAction={{
          content: 'Review purchase draft',
          onAction: handleCreatePo,
          disabled: selectedItems.size === 0 || !canEdit,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setCreatePoModal(false) }]}
        size="large"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="info">
              Select items and quantities. The next screen reviews the supplier, destination, currency and costs before saving.
            </Banner>

            {purchaseError && <Banner tone="critical">{purchaseError}</Banner>}
            <BlockStack gap="300">
              {modalItems.map((item) => (
                <InlineStack key={item.product_id} align="space-between" blockAlign="center">
                  <Checkbox
                    label={`Include ${item.sku}`}
                    checked={selectedItems.has(item.product_id)}
                    onChange={() => toggleItemSelection(item.product_id)}
                  />
                  <div style={{ flex: 1, marginLeft: 8 }}>
                    <BlockStack gap="100">
                      <Text as="span" fontWeight="semibold">{item.sku}</Text>
                      <Text as="span" variant="bodySm" tone="subdued">{item.title}</Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        Stock: {item.available_stock} | Velocity: {formatNumber(item.daily_velocity)}/day
                        {item.qty_on_order > 0 && ` | On Order: ${item.qty_on_order}`}
                      </Text>
                    </BlockStack>
                  </div>
                  <div style={{ width: 100 }}>
                    <TextField
                      label={`Quantity for ${item.sku}`}
                      labelHidden
                      type="number"
                      value={quantities[item.product_id]?.toString() || '0'}
                      onChange={(val) =>
                        setQuantities((prev) => ({ ...prev, [item.product_id]: val }))
                      }
                      autoComplete="off"
                      min={1}
                      disabled={!selectedItems.has(item.product_id)}
                    />
                  </div>
                  <div style={{ width: 80, textAlign: 'right' }}>
                    <Text as="span" variant="bodySm">
                      {formatCurrency(item.unit_cost===null?null:Number(quantities[item.product_id] || 0) * item.unit_cost,item.currency)}
                    </Text>
                  </div>
                </InlineStack>
              ))}
            </BlockStack>

            <TextField
              label="Notes (optional)"
              value={poNotes}
              onChange={setPoNotes}
              multiline={2}
              autoComplete="off"
            />

            <InlineStack align="end">
              <Text as="span" fontWeight="bold">
                Totals: {displayTotals(supplierQuoteTotals(Array.from(selectedItems).map(id=>{const item=modalItems.find(i=>i.product_id===id);return {currency:item?.currency||null,unit_cost:item?.unit_cost??null,quantity:Number(quantities[id]||0)};})))||'No selected quantities'}
              </Text>
            </InlineStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
